import { useRef } from "react";
import { Eye, Heart } from "lucide-react";
import { formatCount } from "~/lib/utils";

export interface ProductVideoCardData {
  feedItemId: string;
  videoUrl: string;
  title: string;
  viewCount?: number;
  likeCount?: number;
}

interface ProductVideoCardProps {
  data: ProductVideoCardData;
  onClick?: () => void;
}

export function ProductVideoCard({ data, onClick }: ProductVideoCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);

  const handleMouseEnter = () => {
    const video = videoRef.current;
    if (!video) return;
    const promise = video.play();
    playPromiseRef.current = promise;
    promise.catch(e => {
      if (e.name !== "AbortError") console.error("Video play failed:", e);
    });
  };

  const handleMouseLeave = () => {
    const video = videoRef.current;
    if (!video) return;
    const p = playPromiseRef.current;
    if (p) {
      p.then(() => {
        video && !video.paused && video.pause();
      })
        .catch(() => {})
        .finally(() => {
          playPromiseRef.current = null;
        });
    } else if (!video.paused) {
      video.pause();
    }
  };

  return (
    <div
      id={`watch-product-video-card-${data.feedItemId}`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className="w-full cursor-pointer"
    >
      {/* Thumbnail */}
      <div
        className="relative aspect-[9/16] overflow-hidden rounded-lg bg-neutral-200"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <video
          ref={videoRef}
          src={data.videoUrl}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      {/* Info */}
      <div className="mt-2 flex flex-col gap-1">
        <p className="text-text-primary line-clamp-2 text-[13px] leading-snug font-medium">{data.title}</p>

        <div className="flex items-center gap-1.5 text-neutral-500">
          {data.viewCount !== undefined && (
            <>
              <Eye className="h-3 w-3" />
              <span className="text-[11px]">{formatCount(data.viewCount)} views</span>
            </>
          )}
          {data.viewCount !== undefined && data.likeCount !== undefined && (
            <span className="text-[11px] text-neutral-300">·</span>
          )}
          {data.likeCount !== undefined && (
            <>
              <Heart className="h-3 w-3" />
              <span className="text-[11px]">{formatCount(data.likeCount)} likes</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
